// Permisos de las escrituras de Inventario v3: cada acción se oculta si el rol no trae su clave.
import { useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import type { Permission } from '@/api/types/permissions';
import { CARD_PERMISSIONS } from '@/domains/retail/tarjetas/constants';

/** Claves de `retail-catalog-business` y, para la fila PCF, de `retail-cards-business`. */
export const INVENTORY_PERMISSIONS = {
    createProduct: 'retail.product.create',
    updateProduct: 'retail.product.update',
    registerEntry: 'retail.inventory.create',
    moveInventory: 'retail.inventory.update',
    readTransfers: 'retail.inventory.lens.read',
    registerCardBatch: CARD_PERMISSIONS.registerBatch,
    writeOffCards: CARD_PERMISSIONS.writeOff,
} as const;

export type InventoryPermissionKey =
    (typeof INVENTORY_PERMISSIONS)[keyof typeof INVENTORY_PERMISSIONS];

export interface InventoryAbilities {
    /** «+ Nuevo»: producto, kit o servicio. */
    canCreateProduct: boolean;
    /** Editar, desactivar y registrar precio. */
    canUpdateProduct: boolean;
    canRegisterEntry: boolean;
    /** Merma o ajuste, y crear, recibir o cancelar traspasos. */
    canMoveInventory: boolean;
    /** Ver los traspasos abiertos. */
    canReadTransfers: boolean;
    /** Agregar un lote de tarjetas PCF. */
    canRegisterCards: boolean;
    /** Dar de baja tarjetas PCF. */
    canWriteOffCards: boolean;
}

export function resolveInventoryAbilities(
    hasPermission: (permission: string) => boolean,
): InventoryAbilities {
    return {
        canCreateProduct: hasPermission(INVENTORY_PERMISSIONS.createProduct),
        canUpdateProduct: hasPermission(INVENTORY_PERMISSIONS.updateProduct),
        canRegisterEntry: hasPermission(INVENTORY_PERMISSIONS.registerEntry),
        canMoveInventory: hasPermission(INVENTORY_PERMISSIONS.moveInventory),
        canReadTransfers: hasPermission(INVENTORY_PERMISSIONS.readTransfers),
        canRegisterCards: hasPermission(INVENTORY_PERMISSIONS.registerCardBatch),
        canWriteOffCards: hasPermission(INVENTORY_PERMISSIONS.writeOffCards),
    };
}

/** Lee los permisos del perfil, la misma fuente que el menú; sin perfil el conjunto viene vacío. */
export function useInventoryAbilities(): InventoryAbilities {
    const { permissions } = useAuth();
    return useMemo(
        () => resolveInventoryAbilities((permission) => permissions.has(permission as Permission)),
        [permissions],
    );
}
